if (!customElements.get('ls-modular-picker')) {
  customElements.define('ls-modular-picker', class extends HTMLElement {
    connectedCallback() {
      this.nativeSelect = this.querySelector('[data-ls-native]');
      this.productInfo = this.closest('product-info');
      if (!this.nativeSelect || !this.productInfo) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.productInfo.addEventListener('click', event => this.handleClick(event), options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-ls-piece-quantity]')) {
          this.productInfo.dataset.lsModularPieceQuantity = event.target.value;
          this.refreshQuantityButtons();
        }
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('input[data-ls-piece-quantity]')) event.stopPropagation();
      }, options);
      this.refresh();
      this.dataset.enhanced = 'true';
      this.querySelector('[data-ls-enhanced]').hidden = false;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.section || !this.active) return;
        this.refresh();
        const key = this.productInfo.dataset.lsModularFocus;
        if (!key) return;
        delete this.productInfo.dataset.lsModularFocus;
        const target = Array.from(this.productInfo.querySelectorAll('[data-ls-focus]'))
          .find(button => button.dataset.lsFocus === key && button.getClientRects().length);
        target?.focus({ preventScroll: true });
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
    }
    get active() {
      return this.productInfo.querySelector('variant-selects') === this.closest('variant-selects');
    }
    get options() { return Array.from(this.nativeSelect.options); }
    get selected() { return this.nativeSelect.selectedOptions[0]; }
    counts(option = this.selected) {
      return (option?.dataset.lsCounts || '').split(',').filter(Boolean).map(Number);
    }
    findConfiguration(counts, puff = false) {
      return this.options.find(option => option.dataset.lsKind === 'composition'
        && option.dataset.lsCounts === counts.join(',')
        && (option.dataset.lsPuff === 'true') === puff);
    }
    available(option) { return !!option && option.dataset.lsAvailable === 'true'; }
    handleClick(event) {
      if (!this.active) return;
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (!this.contains(button) && !button.closest('[data-ls-mode-switch]')) return;
      if (button.hasAttribute('data-ls-quantity-step')) {
        const input = this.querySelector('[data-ls-piece-quantity]');
        if (input.disabled) return;
        if (button.dataset.lsQuantityStep === 'plus') input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      let option;
      if (button.hasAttribute('data-ls-mode')) {
        const kind = button.dataset.lsMode;
        if (this.selected.dataset.lsKind === kind) return;
        const memory = kind === 'extra' ? 'lsModularLastPiece' : 'lsModularLastComposition';
        option = this.options.find(item => item.value === this.productInfo.dataset[memory] && this.available(item))
          || this.options.find(item => item.dataset.lsKind === kind && this.available(item));
      } else if (button.hasAttribute('data-ls-part')) {
        const counts = this.counts();
        counts[Number(button.dataset.lsPart)] = Number(button.dataset.lsCount);
        option = this.findConfiguration(counts, this.selected.dataset.lsPuff === 'true');
      } else if (button.hasAttribute('data-ls-puff')) {
        option = this.findConfiguration(this.counts(), button.dataset.lsPuff === 'true');
      } else if (button.hasAttribute('data-ls-value')) {
        option = this.options.find(item => item.value === button.dataset.lsValue);
      }
      if (!this.available(option) || option.value === this.nativeSelect.value) return;
      this.productInfo.dataset.lsModularFocus = button.dataset.lsFocus;
      this.nativeSelect.value = option.value;
      this.refresh();
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    refreshQuantityButtons() {
      const input = this.querySelector('[data-ls-piece-quantity]');
      const count = Number(input.value);
      this.querySelector('[data-ls-quantity-step="minus"]').disabled = input.disabled || count <= Number(input.min);
      this.querySelector('[data-ls-quantity-step="plus"]').disabled = input.disabled || (input.max !== '' && count >= Number(input.max));
    }
    refresh() {
      const selected = this.selected;
      if (!selected) return;
      const isExtra = selected.dataset.lsKind === 'extra';
      this.dataset.mode = isExtra ? 'extra' : 'composition';
      this.productInfo.dataset.lsModularMode = this.dataset.mode;
      this.productInfo.dataset[isExtra ? 'lsModularLastPiece' : 'lsModularLastComposition'] = selected.value;
      const modeSwitch = this.productInfo.querySelector('[data-ls-mode-switch]');
      if (modeSwitch) {
        modeSwitch.hidden = false;
        modeSwitch.querySelectorAll('[data-ls-mode]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.lsMode === this.dataset.mode));
          button.disabled = !this.options.some(option => option.dataset.lsKind === button.dataset.lsMode && this.available(option));
        });
      }
      const shapes = this.productInfo.querySelector('[id^="LSShapes-"]');
      if (shapes) shapes.hidden = isExtra;
      this.productInfo.querySelectorAll('.ls-placement:not(.ls-placement--details)').forEach(node => { node.hidden = isExtra; });
      this.querySelector('[data-ls-heading]').textContent = isExtra ? this.dataset.pieceHeading : this.dataset.compositionHeading;
      this.querySelector('[data-ls-composition]').hidden = isExtra;
      this.querySelector('[data-ls-canvas]').hidden = isExtra;
      this.querySelector('[data-ls-pieces]').hidden = !isExtra;
      const input = this.querySelector('[data-ls-piece-quantity]');
      input.disabled = !isExtra;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const remembered = Number(this.productInfo.dataset.lsModularPieceQuantity) || min;
      const desired = min + Math.max(0, Math.floor((remembered - min) / step)) * step;
      input.value = String(input.max === '' ? desired : Math.min(desired, Number(input.max)));
      this.refreshQuantityButtons();
      const template = Array.from(this.querySelectorAll('template[data-ls-configuration]'))
        .find(item => item.dataset.lsConfiguration === selected.value);
      if (template && !isExtra) this.querySelector('[data-ls-plan]').replaceChildren(template.content.cloneNode(true));
      this.productInfo.querySelector('ls-modular-addons')?.syncPuffControls?.();
      const counts = this.counts(selected);
      const puff = selected.dataset.lsPuff === 'true';
      if (this.dataset.shape === 'line') this.productInfo.querySelectorAll('[data-ls-puff-guidance]').forEach(node => { node.hidden = isExtra || !puff; });
      this.querySelectorAll('button[data-ls-part]').forEach(button => {
        const index = Number(button.dataset.lsPart);
        const candidateCounts = [...counts];
        candidateCounts[index] = Number(button.dataset.lsCount);
        button.disabled = isExtra || !this.available(this.findConfiguration(candidateCounts, puff));
        button.setAttribute('aria-pressed', String(!isExtra && counts[index] === Number(button.dataset.lsCount)));
      });
      this.querySelectorAll('button[data-ls-puff]').forEach(button => {
        const value = button.dataset.lsPuff === 'true';
        button.disabled = isExtra || !this.available(this.findConfiguration(counts, value));
        button.setAttribute('aria-pressed', String(!isExtra && puff === value));
      });
      this.querySelectorAll('button[data-ls-value]').forEach(button => {
        const option = this.options.find(item => item.value === button.dataset.lsValue);
        button.disabled = !this.available(option);
        button.setAttribute('aria-pressed', String(selected.value === button.dataset.lsValue));
      });
      const piece = this.querySelector('.ls-modular-picker__piece[aria-pressed="true"]');
      const details = this.querySelector('[data-ls-piece-details]');
      if (details) details.textContent = isExtra && piece
        ? [...piece.querySelectorAll('.ls-modular-picker__piece-dimensions, .ls-modular-picker__piece-price')].map(item => item.textContent.trim()).join(' · ')
        : '';
    }
  });
}

